import {
  CallbackProperty,
  Cartesian3,
  Color,
  defined,
  Material,
  PolylineDashMaterialProperty,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
} from "@cesium/engine";

import MeasurementLengthVertical from "../measurement/MeasurementLengthVertical";
import Utils from "../basic/Utils";

/**
 * 수직 거리 측정을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * User input class for vertical length measurement using mouse/keyboard input.
 * @alias InteractionLengthVertical
 * @constructor
 *
 * @param {object} lengthVerticalOption 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Function} [lengthVerticalOption.startCallback] 첫 번째 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating first point.
 * @param {Function} [lengthVerticalOption.addCallback] 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating a point.
 * @param {Function} [lengthVerticalOption.endCallback] 마지막 점을 생성할 때 실행되는 콜백 함수. Callback function executed when creating last point.
 * @param {MeasurementLengthVertical}  [lengthVerticalOption.MeasurementLengthVertical] 수직 거리 및 영역 표시 클래스.<br> vertical calculation and area display class.
 * @example
 * // 샌드캐슬 예제 'DiTAP - Vertical Length Measurement' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const measurementLengthVertical = new Ditap.MeasurementLengthVertical(viewer);
 *
 * // InteractionLengthVertical on/off 실행 시 MeasurementLengthVertical 초기화 필수
 * // measurementLengthVertical 초기화 방법
 * // 1. InteractionLengthVertical 생성 후, MeasurementLengthVertical, callback 함수 대입
 * const interactionLengthVertical = new Ditap.InteractionLengthVertical();
 * interactionLengthVertical.measurementLengthVertical = measurementLengthVertical;
 * interactionLengthVertical.startCallback = () => {};
 * interactionLengthVertical.addCallback = () => {};
 * interactionLengthVertical.endCallback = () => {};
 *
 * // 2. InteractionLengthVertical 생성 시, lengthVerticalOption 지정
 * const lengthVerticalOption = {
 *    measurementLengthVertical: measurementLengthVertical,
 *    startCallback: () => {}
 *    addCallback: () => {}
 *    endCallback: () => {}
 * };
 * const interactionLengthVertical = new Ditap.InteractionLengthVertical(lengthVerticalOption);
 */
class InteractionLengthVertical {
  constructor(lengthVerticalOption) {
    this._measurementLengthVertical = undefined;
    if (
      lengthVerticalOption &&
      lengthVerticalOption.measurementLengthVertical instanceof
        MeasurementLengthVertical
    ) {
      this.measurementLengthVertical =
        lengthVerticalOption.measurementLengthVertical; //원본 클래스
    }
    this._handler = undefined;
    this._moveEntities = []; //move 시 생성되는 Entity 배열
    this._isOn = false; // 핸들러 실행 여부
    this._mousePosition = undefined;
    this._infoLabelEntity = undefined;
    this._verticalPolyline = undefined;
    this._lengthLabelEntity = undefined;
    this._cameraChangedEvent = undefined;
    this._keyDownEvent = undefined;
    this._mousemoveEvent = undefined;
    this._buttonElement = undefined;
    this._polylinePrimitive = undefined;
    this.cancelBtn = undefined; // 핸들러 취소 버튼
    this.removeBtn = undefined; // 한점제거 버튼
    this._isMobile = undefined; // 모바일 여부
    this.drawingPolylineEntities = [];
    this.drawingPointEntities = [];

    if (lengthVerticalOption) {
      if (lengthVerticalOption.startCallback instanceof Function) {
        this._startCallback = lengthVerticalOption.startCallback;
      } else {
        this._startCallback = undefined;
      }
      if (lengthVerticalOption.addCallback instanceof Function) {
        this._addCallback = lengthVerticalOption.addCallback;
      } else {
        this._addCallback = undefined;
      }
      if (lengthVerticalOption.endCallback instanceof Function) {
        this._endCallback = lengthVerticalOption.endCallback;
      } else {
        this._endCallback = undefined;
      }
    }
  }

  /**
   * 수직 거리 계산 및 영역 표시 클래스.<br>
   * Vertical distance calculation and area display class.
   * @type {MeasurementLengthVertical}
   */
  set measurementLengthVertical(measurementLengthVertical) {
    if (
      measurementLengthVertical instanceof MeasurementLengthVertical ||
      measurementLengthVertical === undefined
    ) {
      this._measurementLengthVertical = measurementLengthVertical; // 원본 클래스
    } else {
      console.error("Type of parameter doesn't match");
    }
  }
  get measurementLengthVertical() {
    return this._measurementLengthVertical;
  }

  /*
   * 마우스 무브 시 생성된 엔티티 배열.</br>
   * An array of entities created by mouse movement.
   * @type {Entity}
   */
  set moveEntities(moveEntities) {
    this._moveEntities = moveEntities;
  }
  get moveEntities() {
    return this._moveEntities;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 길이 라벨 라벨 요소.</br>
   * Length label element.
   * @type {Element}
   */
  set lengthLabelEntity(lengthLabelEntity) {
    this._lengthLabelEntity = lengthLabelEntity;
  }
  get lengthLabelEntity() {
    return this._lengthLabelEntity;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   * @readonly
   */
  set isOn(boolean) {
    this._isOn = boolean;
  }
  get isOn() {
    return this._isOn;
  }

  /*
   * 카메라 시점 변경 시 실행 이벤트. </br>
   * Events to run when changing camera viewpoint in window.
   * @type {Function}
   */
  set cameraChangedEvent(cameraChangedEvent) {
    this._cameraChangedEvent = cameraChangedEvent;
  }
  get cameraChangedEvent() {
    return this._cameraChangedEvent;
  }

  /*
   * 윈도우 y 좌표.</br>
   * Window client coordinates y.
   * @type {Cartesian2}
   */
  set clientY(clientY) {
    this._clientY = clientY;
  }
  get clientY() {
    return this._clientY;
  }

  /*
   * 마우스 무브 시 생성되는 수직 폴리라인 엔티티.</br>
   * Vertical Polyline entity generated on mouse movement.
   * @type {Entity}
   */
  set verticalPolyline(verticalPolyline) {
    this._verticalPolyline = verticalPolyline;
  }
  get verticalPolyline() {
    return this._verticalPolyline;
  }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }
  get keyDownEvent() {
    return this._keyDownEvent;
  }

  /*
   * 윈도우에서 마우스 이동 시 실행 이벤트.</br>
   * Events to run when mouse move in the window.
   * @type {Function}
   */
  set mousemoveEvent(mousemoveEvent) {
    this._mousemoveEvent = mousemoveEvent;
  }
  get mousemoveEvent() {
    return this._mousemoveEvent;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼 요소.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }
  get buttonElement() {
    return this._buttonElement;
  }

  /*
   * 마우스 무브 시 생성된 동적 폴리라인 프리미티브.</br>
   * Dynamic polyline primitive generated on mouse move.
   * @type {Primitive}
   */
  set polylinePrimitive(polylinePrimitive) {
    this._polylinePrimitive = polylinePrimitive;
  }
  get polylinePrimitive() {
    return this._polylinePrimitive;
  }

  /**
   * 첫 번째 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating first point.
   * @type {Function}
   */
  set startCallback(startCallback) {
    if (startCallback instanceof Function) {
      this._startCallback = startCallback;
    } else {
      this.startCallback = undefined;
    }
  }
  get startCallback() {
    return this._startCallback;
  }

  /**
   * 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating a point.
   * @type {Function}
   */
  set addCallback(addCallback) {
    if (addCallback instanceof Function) {
      this._addCallback = addCallback;
    } else {
      this.addCallback = undefined;
    }
  }
  get addCallback() {
    return this._addCallback;
  }

  /**
   * 마지막 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating last point.
   * @type {Function}
   */
  set endCallback(endCallback) {
    if (endCallback instanceof Function) {
      this._endCallback = endCallback;
    } else {
      this.endCallback = undefined;
    }
  }
  get endCallback() {
    return this._endCallback;
  }

  /**
   * 수직거리 측정 핸들러 실행. (MeasurementLengthVertical 초기화 필수.)</br>
   * Execute vertical measurement handler. (MeasurementLengthVertical initialization is required.)
   */
  on() {
    if (this.measurementLengthVertical && !this.isOn) {
      this._isMobile = Utils.detectDeviceType();

      this.measurementLengthVertical.viewer.currentEvent = this;
      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      this.handler = new ScreenSpaceEventHandler(
        this.measurementLengthVertical.viewer.canvas
      );

      this.isOn = true;

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.measurementLengthVertical.viewer.container.querySelector(
          ".cesium-viewer"
        ),
        "왼 클릭 또는 터치하여",
        "수직거리를 측정할 선을 그려주세요",
        false,
        true
      );

      // LABEL - 설명 라벨 생성
      this.infoLabel = Utils.createInfoLabel(
        this.measurementLengthVertical.viewer.container.querySelector(
          ".cesium-viewer"
        ),
        "왼 클릭하여 수직거리를 측정할 선을",
        "그려주세요",
        false,
        true
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.removeBtn = this.mobileInfoLabel.removeBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.infoLabel.style.display = "none";
        this.createMobileEvent();
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      // 핸들러 - 실행
      this.createLeftClickHandler();
      this.createCameraMoveEvent();
      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createResizeEvent();
    }
  }

  /**
   * 수직 거리 측정 핸들러 종료. (MeasurementLengthVertical 초기화 필수.)</br>
   * End vertical measurement handler. (MeasurementLengthVertical initialization is required.)
   */
  off() {
    if (this.measurementLengthVertical && this.isOn) {
      const viewer = this.measurementLengthVertical.viewer;

      for (const entity of this.drawingPointEntities) {
        viewer.entities.remove(entity);
      }

      for (const entity of this.drawingPolylineEntities) {
        viewer.entities.remove(entity);
      }

      this.measurementLengthVertical.viewer.currentEvent = undefined;
      // 버튼 비활성화
      this.buttonElement?.classList.remove("on");

      // ENTITY - MOVE시 생성된 엔티티 제거
      for (const moveEntity of this.moveEntities) {
        viewer.entities.remove(moveEntity);
      }

      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      if (this.lengthLabelEntity) {
        this.lengthLabelEntity.remove();
      }

      // PRIMITIVE - 다이나믹 폴리라인 프리미티브 제거
      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      if (this._isMobile) {
        this.removeMobileEvent();
      }

      // 이벤트 제거
      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
      }
      window.removeEventListener("keydown", this.keyDownEvent);
      window.removeEventListener("mousemove", this.mousemoveEvent);

      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }

      // 카메라 이벤트 삭제
      viewer.scene.camera.changed.removeEventListener(this.cameraChangedEvent);

      this.handler = undefined;
      this.moveEntities = [];
      this.isOn = false;
      this.mousePosition = undefined;
      this.lengthLabelEntity = undefined;
      this.verticalPolyline = undefined;
      this.keyDownEvent = undefined;
      this.polylinePrimitive = undefined;
      this.measurementLengthVertical = undefined;
      this.cameraChangedEvent = undefined;
      this.cancelBtn = undefined;
      this.removeBtn = undefined;
      this._isMobile = undefined;
      this.drawingPointEntities = [];
      this.drawingPolylineEntities = [];
      this.infoLabelEntity = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();
        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;
            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.removeBtn = this.mobileInfoLabel.removeBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.lengthLabelEntity.classList.add("hide");
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            for (const entity of this.moveEntities) {
              this.measurementLengthVertical.viewer.entities.remove(entity);
            }
            if (this.polylinePrimitive) {
              this.measurementLengthVertical.viewer.scene.primitives.remove(
                this.polylinePrimitive
              );
            }

            this.moveEntities = [];
            this.polylinePrimitive = undefined;

            this.createMobileEvent();
          } else {
            this.lengthLabelEntity.classList.remove("hide");
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();

            // this.moveLengthLabel();
          }
        }
      }

      this.moveLengthLabel();
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.destroy();
      })
    );

    this.removeBtn.addEventListener(
      "click",
      (this.removeEvent = () => {
        this.removePoint();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
      this.cancelBtn = undefined;
      this.cancelEvent = undefined;
    }
    if (this.removeEvent) {
      this.removeBtn.removeEventListener("click", this.removeEvent);
      this.removeBtn = undefined;
      this.removeEvent = undefined;
    }
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.abort();
    this.off();
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const viewer = this.measurementLengthVertical.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      if (this.isOn) {
        const pickposition = Utils.pickPosition(
          e.position,
          this.measurementLengthVertical.viewer
        );

        const startPosition = this.measurementLengthVertical.positions[0];

        if (defined(pickposition)) {
          this.mousePosition = pickposition;
          // 점 생성 시 콜백함수 실행
          if (this.addCallback && this.measurementLengthVertical) {
            this.addCallback(this.measurementLengthVertical);
          }
          this.measurementLengthVertical.positions.push(pickposition);
          if (startPosition === undefined) {
            // POINT - 시작하는 지점에 POINT 엔티티 생성
            const startPoint = Utils.createPoint({
              viewer: viewer,
              position: pickposition,
            });
            this.drawingPointEntities.push(startPoint);

            // 시작점에서 수평거리 계산
            const distance = this.measurementLengthVertical.calculateVerticalLength(
              pickposition,
              this.mousePosition
            );

            // 시작점에서 수평 좌표값 계산
            const verticalPosition = this.measurementLengthVertical.getVerticalPosition(
              pickposition,
              this.mousePosition
            );

            // 원본 클래스에 시작좌표, 시작포인트, 수평거리, 수평좌표 저장
            this.measurementLengthVertical.pointEntities.push(startPoint);
            this.measurementLengthVertical.distance = distance;
            this.measurementLengthVertical.verticalPosition = verticalPosition;

            // POLYLINE - MOVE시 생성되는 수평 폴리라인 엔티티
            const verticalPolyline = Utils.createPolyline({
              viewer: viewer,
              positions: new CallbackProperty(() => {
                if (!this._isMobile) {
                  return [
                    pickposition,
                    this.measurementLengthVertical.verticalPosition,
                  ];
                }
              }, false),
            });

            this.drawingPolylineEntities.push(verticalPolyline);

            // POLYLINE - MOVE시 생성되는 수평 폴리라인 엔티티
            const horizontalPolyline = Utils.createPolyline({
              viewer: viewer,
              positions: new CallbackProperty(() => {
                if (!this._isMobile) {
                  const left = this.measurementLengthVertical.verticalPosition;
                  const right = this.mousePosition;
                  const middle = Cartesian3.lerp(
                    left,
                    right,
                    9 / 10,
                    new Cartesian3()
                  );
                  return [left, middle];
                }
              }, false),
              material: new PolylineDashMaterialProperty({
                color: Color.WHITE,
              }),
            });
            this.drawingPolylineEntities.push(horizontalPolyline);

            let first = true;
            // POINT 생성 - MOVE시 생성되는 엔티티
            const point = Utils.createPoint({
              viewer: viewer,
              position: new CallbackProperty(() => {
                if (first) {
                  first = false;
                  return this.measurementLengthVertical.verticalPosition;
                }
                if (!this._isMobile) {
                  return this.measurementLengthVertical.verticalPosition;
                }
              }, false),
            });
            this.drawingPointEntities.push(point);

            // LABEL 생성 - move시 생성되는 엔티티
            const label = Utils.createTotalLabel(viewer, distance);
            this.lengthLabelEntity = label;
            this.measurementLengthVertical.labelHeight = label.clientHeight;
            Utils.changeLabelLocation(
              viewer,
              Cartesian3.midpoint(
                pickposition,
                this.measurementLengthVertical.verticalPosition,
                new Cartesian3()
              ),
              label,
              label.clientHeight
            );

            this._isMobile = Utils.detectDeviceType();
            if (this._isMobile) {
              this.lengthLabelEntity.classList.add("hide");
            }

            // 첫번째 점 생성 시 콜백함수 실행
            if (this.startCallback && this.measurementLengthVertical) {
              this.startCallback(this.measurementLengthVertical);
            }
          } else {
            this.endHandler();
          }
        }
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 직선 거리 측정 종료.<br>
   * End straight line distance measurement.
   */
  endHandler() {
    this.measurementLengthVertical.createLengthVertical();

    // 마지막 점 생성 시 콜백함수 실행
    if (this.endCallback && this.measurementLengthVertical) {
      this.endCallback(this.measurementLengthVertical);
    }

    this.off();
  }

  /*
   * 길이 라벨 위치 변경.<br>
   * Change the position of the length label.
   */
  moveLengthLabel() {
    if (this.lengthLabelEntity && !this._isMobile) {
      const centerPosition = Cartesian3.midpoint(
        this.measurementLengthVertical.verticalPosition,
        this.measurementLengthVertical.positions[0],
        new Cartesian3()
      );
      const labelHeight = this.lengthLabelEntity.clientHeight;
      this.measurementLengthVertical.labelHeight = labelHeight;
      this.measurementLengthVertical.centerPosition = centerPosition;
      Utils.changeLabelLocation(
        this.measurementLengthVertical.viewer,
        centerPosition,
        this.lengthLabelEntity,
        labelHeight
      );
    }
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.measurementLengthVertical.viewer;
    const handler = this.handler;

    // MOUSE_MOVE
    handler.setInputAction((e) => {
      if (this.isOn) {
        const movePosition = Utils.pickPosition(e.endPosition, viewer);
        if (defined(movePosition)) {
          this.mousePosition = movePosition;
          // 도움말 라벨 좌표 이동
          Utils.changeLabelLocation(
            viewer,
            this.mousePosition,
            this.infoLabelEntity
          );

          const startPosition = this.measurementLengthVertical.positions[0];

          // 시작포인트가 있을 때
          if (startPosition) {
            // 수평거리 계산
            let distance = this.measurementLengthVertical.calculateVerticalLength(
              startPosition,
              this.mousePosition
            );

            // 시작점에서 수평 좌표값 계산
            const verticalPosition = this.measurementLengthVertical.getVerticalPosition(
              startPosition,
              this.mousePosition
            );

            // 원본클레스에 수평거리, 수평좌표 저장
            this.measurementLengthVertical.distance = distance;
            this.measurementLengthVertical.verticalPosition = verticalPosition;

            if (this.lengthLabelEntity) {
              distance = Utils.printCommaSeparator(distance);
              this.lengthLabelEntity.querySelector(
                ".ditap-card-title"
              ).textContent = `${distance}m`;

              this.moveLengthLabel();
            }

            if (this.polylinePrimitive) {
              viewer.scene.primitives.remove(this.polylinePrimitive);
            }
            // // POLYLINE - 동적 수평 포인트<->수평 라인, 일반
            const horizontalPolyline = Utils.createPolylinePrimitive({
              viewer: viewer,
              positions: [
                this.measurementLengthVertical.verticalPosition,
                this.mousePosition,
              ],
              material: Material.fromType("PolylineDash", {
                color: Color.WHITE,
              }),
            });

            this.polylinePrimitive = horizontalPolyline;
          }
        } else {
          this.infoLabelEntity.classList.add("hide");
        }
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      if (this.measurementLengthVertical.positions.length > 0) {
        this.removePoint();
      } else {
        this.abort();
        this.off();
      }
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (e) => {
        this.onKeyDown(e);
      })
    );
  }

  /**
   * 측정 중 중단.</br>
   * Interrupt during measurement.
   */
  abort() {
    // measurementLengthVertical 삭제
    this.measurementLengthVertical?.destroy();
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    if (event.key === "Escape") {
      // ESC 클릭 : Entity 제거, 모든 핸들러 종료
      this.abort();
      this.off();
    } else if (event.key === "Backspace") {
      this.removePoint();
    }
  }

  /*
   * 측정 중인 한 점 제거.<br>
   * Remove the point being measured.
   */
  removePoint() {
    // Backspace : 한 점 제거
    // POINT - 마지막 엔티티, 값 삭제

    // 한 점 이상일 경우에만 동작
    if (this.measurementLengthVertical.positions.length > 0) {
      const viewer = this.measurementLengthVertical.viewer;

      for (const entity of this.drawingPointEntities) {
        viewer.entities.remove(entity);
      }
      for (const entity of this.drawingPolylineEntities) {
        viewer.entities.remove(entity);
      }
      this.drawingPointEntities = [];
      this.drawingPolylineEntities = [];

      this.measurementLengthVertical.removePointEntity(
        this.measurementLengthVertical.pointEntities[
          this.measurementLengthVertical.pointEntities.length - 1
        ]
      );

      // POSITIONS - 마지막 좌표값 삭제
      this.measurementLengthVertical.removePositions(
        this.measurementLengthVertical.positions[
          this.measurementLengthVertical.positions.length - 1
        ]
      );

      this.lengthLabelEntity.remove();

      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      // ENTITY - MOVE시 생성된 엔티티 제거
      for (const moveEntity of this.moveEntities) {
        viewer.entities.remove(moveEntity);
      }
      this.moveEntities = [];

      if (this.lengthLabelEntity) {
        this.lengthLabelEntity.remove();
        this.lengthLabelEntity = undefined;
      }
    }
  }

  /*
   * 카메라 시점 이동 시 이벤트 생성. </br>
   * Create an event when moving the camera point of view.
   */
  createCameraMoveEvent() {
    const viewer = this.measurementLengthVertical.viewer;
    this.cameraChangedEvent = () => {
      this.moveLengthLabel();
    };
    viewer?.scene.camera.changed.addEventListener(this.cameraChangedEvent);
  }
}
export default InteractionLengthVertical;
